'use client';

import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import { View, Pressable, Modal, Alert, useWindowDimensions } from 'react-native';
import { Input, Button, YStack, XStack, SizableText, Card, H3 } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { debounce } from 'lodash';

// Define the methods you want to expose to the parent component
export type DocumentDomEditorHandle = {
  getContent: () => string | null;
  setContent: (html: string) => void;
  insertTemplateField: (fieldName: string) => void;
};

type DocumentDomEditorProps = {
  saveFn: (content: string) => Promise<void>;
  backFn?: () => void;
  hideBack?: boolean;
  initialContent?: string;
  onTemplateFieldInserted?: (fieldName: string) => void;
};

const DocumentDomEditor = forwardRef<DocumentDomEditorHandle, DocumentDomEditorProps>(
  function DocumentDomEditor(
    { hideBack, saveFn, backFn, initialContent = '', onTemplateFieldInserted },
    ref
  ) {
    const theme = themeSelector();
    const editorRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const [showFieldModal, setShowFieldModal] = useState(false);
    const [newFieldName, setNewFieldName] = useState('');
    const { width } = useWindowDimensions();
    const isInitialized = useRef(false);
    const initialContentSet = useRef(false);
    const isInsertingField = useRef(false);

    // Create a stable debounced save function using useRef
    const debouncedSaveRef = useRef(
      debounce((content: string) => {
        saveFn(content);
      }, 1000)
    );

    // Update the debounced save function when saveFn changes
    useEffect(() => {
      debouncedSaveRef.current = debounce((content: string) => {
        saveFn(content);
      }, 1000);
    }, [saveFn]);

    // Initialize Quill with custom toolbar
    useEffect(() => {
      if (editorRef.current && toolbarRef.current && !quillRef.current && !isInitialized.current) {
        isInitialized.current = true;
        // Custom toolbar configuration
        const toolbarOptions = [
          [{ 'font': [] }],
          [{ 'size': ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'script': 'sub'}, { 'script': 'super' }],
          [{ 'header': 1 }, { 'header': 2 }, 'blockquote', 'code-block'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'indent': '-1'}, { 'indent': '+1' }],
          [{ 'direction': 'rtl' }],
          [{ 'align': [] }],
          ['link'],
          ['clean'],
          ['template-field'] // Custom button
        ];

        const quill = new Quill(editorRef.current, {
          theme: 'snow',
          modules: {
            toolbar: {
              container: toolbarOptions,
              handlers: {
                'template-field': () => {
                  setShowFieldModal(true);
                }
              }
            }
          },
          placeholder: 'Start writing your document template...',
        });

        quillRef.current = quill;

        // Set initial content if provided
        if (initialContent && !initialContentSet.current) {
          quill.clipboard.dangerouslyPasteHTML(initialContent);
          initialContentSet.current = true;
        }

        // Add content change listener with debounced save
        quill.on('text-change', (delta, oldDelta, source) => {
          // Skip save if we're programmatically inserting a field
          if (isInsertingField.current) {
            return;
          }
          const content = quill.root.innerHTML;
          debouncedSaveRef.current(content);
        });

        // Add custom button styling after initialization
        setTimeout(() => {
          const customButton = document.querySelector('.ql-template-field');
          if (customButton) {
            customButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
              <circle cx="12" cy="13" r="3"/>
            </svg>`;
            customButton.setAttribute('title', 'Insert Template Field');
          }

          // Add custom CSS for template fields
          const style = document.createElement('style');
          style.textContent = `
            .ql-editor {
              position: relative;
              height: auto !important;
              min-height: 200px !important;
              max-height: 300px !important;
              overflow-y: auto !important;
              overflow-x: hidden !important;
            }
            .ql-container {
              height: 100% !important;
              max-height: 100% !important;
              display: flex;
              flex-direction: column;
            }
            .ql-toolbar {
              border-bottom: 1px solid #ccc;
              flex-shrink: 0;
            }
            .ql-template-field {
              background-color: #f5f5f5 !important;
            }
            .ql-template-field:hover {
              background-color: #e0e0e0 !important;
            }
            /* Style handlebars expressions in the editor */
            .ql-editor {
              background-image: linear-gradient(transparent, transparent);
            }
          `;
          document.head.appendChild(style);
          
          // Add a mutation observer to style handlebars expressions
          const observer = new MutationObserver(() => {
            if (quillRef.current) {
              const editor = quillRef.current.root;
              const text = editor.textContent || '';
              const handlebarsRegex = /\{\{\{[^}]+\}\}\}/g;
              
              // This is a simple approach - for production you might want more sophisticated styling
              if (handlebarsRegex.test(text)) {
                // Template fields are present, the CSS will handle basic styling
              }
            }
          });
          
          if (quillRef.current) {
            observer.observe(quillRef.current.root, { childList: true, subtree: true, characterData: true });
          }
        }, 100);
      }

      return () => {
        // Cancel any pending debounced saves
        debouncedSaveRef.current.cancel();
        isInitialized.current = false;
        quillRef.current = null;
        if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
      };
    }, []);

    // Handle content updates when initialContent changes
    useEffect(() => {
      if (quillRef.current && initialContent && !initialContentSet.current) {
        const currentContent = quillRef.current.root.innerHTML;
        // Only update if content is actually different
        if (currentContent !== initialContent) {
          quillRef.current.clipboard.dangerouslyPasteHTML(initialContent);
          initialContentSet.current = true;
        }
      }
    }, [initialContent]);

    // Insert template field function (memoized to prevent recreations)
    const insertTemplateField = useCallback((fieldName: string) => {
      if (quillRef.current) {
        // Set flag to prevent text-change from triggering save
        isInsertingField.current = true;

        const range = quillRef.current.getSelection() || { index: quillRef.current.getLength(), length: 0 };
        
        // Create the template field text
        const templateFieldText = `{{{${fieldName}}}}`;
        
        // Insert the text at the cursor position without any formatting
        quillRef.current.insertText(range.index, templateFieldText, 'user');
        
        // Move cursor after the inserted field
        const newCursorPosition = range.index + templateFieldText.length;
        setTimeout(() => {
          if (quillRef.current) {
            quillRef.current.setSelection(newCursorPosition, 0);
          }
          // Reset flag after insertion is complete
          isInsertingField.current = false;
        }, 10);
        
        // Notify parent component
        if (onTemplateFieldInserted) {
          onTemplateFieldInserted(fieldName);
        }
      }
    }, [onTemplateFieldInserted]);

    // Handle adding new template field (memoized to prevent recreations)
    const handleAddTemplateField = useCallback(() => {
      if (newFieldName.trim()) {
        // Validate field name (only letters, numbers, and underscores)
        const validFieldName = newFieldName.trim().replace(/[^a-zA-Z0-9_]/g, '_');
        if (validFieldName !== newFieldName.trim()) {
          Alert.alert(
            'Field Name Modified',
            `Field name has been modified to: ${validFieldName}`,
            [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Use Modified Name', onPress: () => {
                insertTemplateField(validFieldName);
                setNewFieldName('');
                setShowFieldModal(false);
              }}
            ]
          );
        } else {
          insertTemplateField(validFieldName);
          setNewFieldName('');
          setShowFieldModal(false);
        }
      }
    }, [newFieldName, insertTemplateField]);

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
      getContent: () => {
        return quillRef.current ? quillRef.current.root.innerHTML : null;
      },
      setContent: (html: string) => {
        if (quillRef.current) {
          quillRef.current.root.innerHTML = html;
        }
      },
      insertTemplateField: insertTemplateField,
    }), [insertTemplateField]);

    const handleSave = useCallback(async () => {
      const content = quillRef.current?.root.innerHTML;
      if (content !== null && content !== undefined) {
        console.log("Content to save:", content);
        await saveFn(content);
      } else {
        console.error("Editor is not initialized or is empty.");
      }
    }, [saveFn]);

    return (
      <YStack flex={1} backgroundColor={theme ? "#ffffff" : "#0f172a"} padding="$4" gap="$4">
        <Card
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$4"
          elevate
        >
          <YStack gap="$3">
            {/* Custom Toolbar */}
            <div ref={toolbarRef} id="toolbar">
              <span className="ql-formats">
                <select className="ql-font"></select>
                <select className="ql-size"></select>
              </span>
              <span className="ql-formats">
                <button className="ql-bold"></button>
                <button className="ql-italic"></button>
                <button className="ql-underline"></button>
                <button className="ql-strike"></button>
              </span>
              <span className="ql-formats">
                <select className="ql-color"></select>
                <select className="ql-background"></select>
              </span>
              <span className="ql-formats">
                <button className="ql-script" value="sub"></button>
                <button className="ql-script" value="super"></button>
              </span>
              <span className="ql-formats">
                <button className="ql-header" value="1"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-code-block"></button>
              </span>
              <span className="ql-formats">
                <button className="ql-list" value="ordered"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
              </span>
              <span className="ql-formats">
                <button className="ql-direction" value="rtl"></button>
                <select className="ql-align"></select>
              </span>
              <span className="ql-formats">
                <button className="ql-link"></button>
              </span>
              <span className="ql-formats">
                <button className="ql-template-field" title="Insert Template Field"></button>
              </span>
              <span className="ql-formats">
                <button className="ql-clean"></button>
              </span>
            </div>
            
            {/* Editor Container */}
            <div ref={editorRef} style={{ 
              height: '100%',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${theme ? '#cbd5e1' : '#475569'}`,
              borderRadius: '12px',
              backgroundColor: theme ? '#ffffff' : '#0f172a'
            }}></div>
          </YStack>
        </Card>

        {/* Action Buttons */}
        <XStack gap="$3" justifyContent="center" flexWrap="wrap">
          {!hideBack && (
            <Button
              backgroundColor="#ef4444"
              color="white"
              size="$4"
              onPress={backFn}
              borderRadius={12}
              paddingHorizontal="$5"
              paddingVertical="$3"
            >
              <XStack alignItems="center" gap="$2">
                <MaterialIcons name="cancel" size={20} color="white" />
                <SizableText color="white" fontWeight="600" size="$4">
                  Cancel
                </SizableText>
              </XStack>
            </Button>
          )}
          <Button
            backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
            color="white"
            size="$4"
            onPress={handleSave}
            borderRadius={12}
            paddingHorizontal="$5"
            paddingVertical="$3"
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons name="save" size={20} color="white" />
              <SizableText color="white" fontWeight="600" size="$4">
                Save Changes
              </SizableText>
            </XStack>
          </Button>
        </XStack>

        {/* Template Field Modal */}
        <Modal
          visible={showFieldModal}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setShowFieldModal(false)}
        >
          <View style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}>
            <Card
              width={width * 0.9}
              maxWidth={500}
              borderRadius={20}
              padding="$4"
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
            >
              <YStack gap="$4">
                <XStack alignItems="center" justifyContent="space-between">
                  <H3 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                    Insert Template Field
                  </H3>
                  <Pressable onPress={() => setShowFieldModal(false)}>
                    <MaterialIcons name="close" size={24} color={theme ? "#64748b" : "#94a3b8"} />
                  </Pressable>
                </XStack>

                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  Enter a field name that will be replaced with actual data when the template is used.
                </SizableText>

                <Input
                  placeholder="e.g., customerName, invoiceDate, totalAmount"
                  value={newFieldName}
                  onChangeText={setNewFieldName}
                  autoFocus
                  size="$4"
                  backgroundColor={theme ? "#f8fafc" : "#0f172a"}
                  borderColor={theme ? "#cbd5e1" : "#475569"}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  borderRadius={12}
                  paddingHorizontal="$3"
                  paddingVertical="$3"
                />

                <Card
                  backgroundColor={theme ? "#f0f9ff" : "#0c4a6e"}
                  borderColor={theme ? "#bfdbfe" : "#075985"}
                  borderRadius={12}
                  padding="$3"
                >
                  <SizableText
                    size="$3"
                    color={theme ? "#0369a1" : "#7dd3fc"}
                  >
                    Field names should only contain letters, numbers, and underscores.
                    {'\n'}This will appear as: <SizableText fontWeight="600">{`{{{${newFieldName || 'fieldName'}}}}`}</SizableText>
                  </SizableText>
                </Card>

                <XStack gap="$3" justifyContent="flex-end">
                  <Button
                    backgroundColor={theme ? "#f1f5f9" : "#334155"}
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$4"
                    onPress={() => {
                      setNewFieldName('');
                      setShowFieldModal(false);
                    }}
                    borderRadius={12}
                    paddingHorizontal="$4"
                  >
                    <SizableText color={theme ? "#64748b" : "#94a3b8"} fontWeight="600">
                      Cancel
                    </SizableText>
                  </Button>
                  <Button
                    backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                    color="white"
                    size="$4"
                    onPress={handleAddTemplateField}
                    disabled={!newFieldName.trim()}
                    opacity={!newFieldName.trim() ? 0.6 : 1}
                    borderRadius={12}
                    paddingHorizontal="$4"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="add" size={20} color="white" />
                      <SizableText color="white" fontWeight="600">
                        Insert Field
                      </SizableText>
                    </XStack>
                  </Button>
                </XStack>
              </YStack>
            </Card>
          </View>
        </Modal>
      </YStack>
    );
  }
);

export default DocumentDomEditor;
